import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  Typography,
  Toolbar,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardMedia,
  Stack,
  Divider,
  Link as MuiLink,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  Security as SecurityIcon,
  Timeline as TimelineIcon,
  CardGiftcard as RewardIcon,
  Phone as PhoneIcon,
  Facebook as FacebookIcon,
  X as XIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { GetStaticProps } from 'next';
import { getAllBlogPosts } from '../utils/blogUtils';
import { getFAQData, FAQItem } from '../utils/faqUtils';
import ReactMarkdown from 'react-markdown';
import { useAnalytics } from '../components/AnalyticsProvider';

// Countdown Component - COMMENTED OUT
// const CountdownTimer = ({ targetDate, onCountdownEnd }: { targetDate: Date, onCountdownEnd: () => void }) => {
//   const [timeLeft, setTimeLeft] = useState({
//     days: 0,
//     hours: 0,
//     minutes: 0,
//     seconds: 0
//   });

//   useEffect(() => {
//     const calculateTimeLeft = () => {
//       const difference = targetDate.getTime() - new Date().getTime();
      
//       if (difference > 0) {
//         setTimeLeft({
//           days: Math.floor(difference / (1000 * 60 * 60 * 24)),
//           hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
//           minutes: Math.floor((difference / 1000 / 60) % 60),
//           seconds: Math.floor((difference / 1000) % 60)
//         });
//       } else {
//         onCountdownEnd();
//       }
//     };

//     calculateTimeLeft();
//     const timer = setInterval(calculateTimeLeft, 1000);

//     return () => clearInterval(timer);
//   }, [targetDate, onCountdownEnd]);

//   return (
//     <Box sx={{ textAlign: 'center' }}>
//       <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1F2937' }}>
//         App Launch Countdown
//       </Typography>
//       <Stack direction="row" spacing={2} justifyContent="center">
//         <Box sx={{ textAlign: 'center' }}>
//           <Paper
//             sx={{
//               width: 60,
//               height: 60,
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               backgroundColor: '#6C5DD3',
//               color: 'white',
//               fontWeight: 700,
//               fontSize: '1.5rem',
//               borderRadius: 2
//             }}
//           >
//             {timeLeft.days.toString().padStart(2, '0')}
//           </Paper>
//           <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#6B7280' }}>
//             Days
//           </Typography>
//         </Box>
//         <Box sx={{ textAlign: 'center' }}>
//           <Paper
//             sx={{
//               width: 60,
//               height: 60,
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               backgroundColor: '#6C5DD3',
//               color: 'white',
//               fontWeight: 700,
//               fontSize: '1.5rem',
//               borderRadius: 2
//             }}
//           >
//             {timeLeft.hours.toString().padStart(2, '0')}
//           </Paper>
//           <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#6B7280' }}>
//             Hours
//           </Typography>
//         </Box>
//         <Box sx={{ textAlign: 'center' }}>
//           <Paper
//             sx={{
//               width: 60,
//               height: 60,
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               backgroundColor: '#6C5DD3',
//               color: 'white',
//               fontWeight: 700,
//               fontSize: '1.5rem',
//               borderRadius: 2
//             }}
//           >
//             {timeLeft.minutes.toString().padStart(2, '0')}
//           </Paper>
//           <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#6B7280' }}>
//             Minutes
//           </Typography>
//         </Box>
//         <Box sx={{ textAlign: 'center' }}>
//           <Paper
//             sx={{
//               width: 60,
//               height: 60,
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               backgroundColor: '#6C5DD3',
//               color: 'white',
//               fontWeight: 700,
//               fontSize: '1.5rem',
//               borderRadius: 2
//             }}
//           >
//             {timeLeft.seconds.toString().padStart(2, '0')}
//           </Paper>
//           <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#6B7280' }}>
//             Seconds
//           </Typography>
//         </Box>
//       </Stack>
//     </Box>
//   );
// };

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #EDE9FF 0%, #F3F0FF 100%)',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #6C5DD3 0%, #A78BFA 100%)',
  color: 'white',
  padding: '12px 24px',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '16px',
  boxShadow: '0px 4px 15px rgba(108, 93, 211, 0.3)',
  '&:hover': {
    background: 'linear-gradient(90deg, #5A4BC4 0%, #9673E6 100%)',
    boxShadow: '0px 6px 20px rgba(108, 93, 211, 0.4)',
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  background: '#F5F3FF',
  borderRadius: '16px',
  padding: '24px',
  height: '100%',
  boxShadow: '4px 4px 25px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '8px 8px 30px rgba(0, 0, 0, 0.12)',
  },
}));

const PhonePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: '560px',
  margin: '0 auto',
  '& .phone-frame': {
    background: 'none',
    borderRadius: '0px',
    padding: 0,
    boxShadow: 'none',
  },
  '& .phone-screen': {
    background: 'none',
    borderRadius: 0,
    overflow: 'visible',
    aspectRatio: '9/19.5',
  },
}));

const StatsBox = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #6C5DD3 0%, #A78BFA 100%)',
  color: 'white',
  padding: '32px',
  borderRadius: '24px',
  textAlign: 'center',
  boxShadow: '0px 8px 32px rgba(108, 93, 211, 0.3)',
}));

interface HomePageProps {
  blogPosts: any[];
  faqs: FAQItem[];
}

const SPTDrivePortal: React.FC<HomePageProps> = ({ blogPosts: initialBlogPosts, faqs: initialFaqs }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(1);
  const { 
    trackDownloadClick, 
    trackAppStoreClick, 
    trackGooglePlayClick, 
    trackVideoStart,
    trackVideoComplete,
    trackVideoPause,
    trackFAQExpand,
    trackNavigationClick,
    trackExternalLinkClick,
    trackShowMoreClick,
    trackShowLessClick
  } = useAnalytics();
  // const [countdownEnded, setCountdownEnded] = useState(false); // COMMENTED OUT
  
  
  // Target date: September 9, 2025, 9:09 AM PDT - COMMENTED OUT
  // PDT is UTC-7, so 9:09 AM PDT = 16:09 UTC (9:09 + 7 hours)
  // const targetDate = new Date('2025-09-09T16:09:00Z'); // Production date


  // Custom scroll function that accounts for AppBar height
  const scrollToSection = (elementId: string) => {
    const element = document.getElementById(elementId);
    
    // Try multiple selectors to find AppBar
    const appBar = document.querySelector('[role="banner"]') as HTMLElement || 
                   document.querySelector('.MuiAppBar-root') as HTMLElement ||
                   document.querySelector('header') as HTMLElement;
    
    if (element) {
      const appBarHeight = appBar ? appBar.offsetHeight : 64; // Fallback to default height
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - appBarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const features = [
    {
      icon: <PhoneIcon sx={{ fontSize: 32, color: 'white' }} />,
      title: 'Download the Zenlit App',
      description: 'Get started in minutes. Available on iOS and Android.',
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 32, color: 'white' }} />,
      title: 'Drive Smart to Build Your ZenScore',
      description: 'Our advanced technology measures your driving habits in real time.',
    },
    {
      icon: <RewardIcon sx={{ fontSize: 32, color: 'white' }} />,
      title: 'Earn ZNLT & Redeem Rewards',
      description: 'User your tokens for exclusive perks, gift cards, discounts, and more.',
    },
  ];

  // FAQ data is loaded via getStaticProps

  const [blogPosts, setBlogPosts] = useState(initialBlogPosts.slice(0, 3));
  const [faqs, setFaqs] = useState(initialFaqs);
  const [showAllFaqs, setShowAllFaqs] = useState(false);

  // Blog posts are loaded via getStaticProps

  const screenshots = [
    {
      src: '/images/Picture2.png',
      label: 'Tokens for Smart Driving',
      description: 'Track your safety token balance and monthly progress. Refer friends for bonus rewards.',
    },
    {
      src: '/images/Picture1.png',
      label: 'Driving Performance',
      description: 'Monitor your ZenScore, safe miles driven, and your ranking and contribution in the Zenlit community.',
    },
    {
      src: '/images/Picture3.png',
      label: 'Detailed Trip Insights',
      description: 'View complete trip breakdowns with mapped routes and safety events for each journey.',
    },
  ];

  return (
    <>
      <Head>
        <title>Zenlit - Drive Smart, Earn Rewards, Build Safer Roads</title>
        <meta name="description" content="Turn your safe driving into rewards with Zenlit. Join thousands of drivers making roads safer while earning tokens." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/images/logo-coin.png" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap"
        />
      </Head>

      {/* Header */}
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: 'white', 
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
          <Toolbar sx={{ justifyContent: 'space-between', py: 1, maxWidth: '1920px', mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="img"
                  src="/images/logo-coin.png"
                  alt="Zenlit Logo"
                  sx={{
                    width: { xs: 40, md: 58 },
                    height: { xs: 40, md: 58 },
                  }}
                />
              <Typography 
                variant="h4" 
                component="div" 
                sx={{ 
                  fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: 700, 
                  color: '#6C5DD3',
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                Zenlit
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <MuiLink 
                  component="button"
                  onClick={() => {
                    trackNavigationClick('How It Works', '#how-it-works');
                    scrollToSection('how-it-works');
                  }}
                  color="inherit"
                  underline="none"
                  sx={{ 
                    fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                    color: '#1F2937',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'inherit'
                  }}
                >
                  How It Works
                </MuiLink>
                <MuiLink 
                  component="button"
                  onClick={() => {
                    trackNavigationClick('Blog', '#blog');
                    scrollToSection('blog');
                  }}
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                    color: '#1F2937',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'inherit'
                  }}
                >
                  Blog
                </MuiLink>
                <MuiLink 
                  component="button"
                  onClick={() => {
                    trackNavigationClick('Our Mission', '#mission');
                    scrollToSection('mission');
                  }}
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                    color: '#1F2937',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'inherit'
                  }}
                >
                  Our Mission
                </MuiLink>
                <MuiLink 
                  component="button"
                  onClick={() => {
                    trackNavigationClick('FAQ', '#faq');
                    scrollToSection('faq');
                  }}
                  color="inherit" 
                  underline="none"
                  sx={{ 
                    fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
                    color: '#1F2937',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'inherit'
                  }}
                >
                  FAQ
                </MuiLink>
                
                {/* Social Icons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    size="small" 
                    sx={{ color: '#9CA3AF' }}
                    component="a"
                    href="https://www.facebook.com/GetZenlit"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackExternalLinkClick('facebook', 'https://www.facebook.com/GetZenlit')}
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    sx={{ color: '#9CA3AF' }}
                    component="a"
                    href="https://x.com/getzenlit"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackExternalLinkClick('x_twitter', 'https://x.com/getzenlit')}
                  >
                    <XIcon />
                  </IconButton>

                </Box>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                sx={{ 
                  color: '#1F2937',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MenuIcon 
                    sx={{
                      position: 'absolute',
                      opacity: mobileMenuOpen ? 0 : 1,
                      transform: mobileMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'all 0.3s ease-in-out',
                    }}
                  />
                  <CloseIcon 
                    sx={{
                      position: 'absolute',
                      opacity: mobileMenuOpen ? 1 : 0,
                      transform: mobileMenuOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'all 0.3s ease-in-out',
                    }}
                  />
                </Box>
              </IconButton>
            )}
          </Toolbar>
        </Box>
      </AppBar>

      {/* Mobile Menu */}
      {isMobile && mobileMenuOpen && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          backgroundColor: 'white', 
          zIndex: 1200, 
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)', 
          borderBottom: '1px solid #E5E7EB', 
          mt: 8 
        }}>
          <Box sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', maxWidth: '1920px', mx: 'auto', py: 2 }}>
              <Stack spacing={2} sx={{ alignItems: 'flex-end' }}>
                <MuiLink component="button" onClick={() => { trackNavigationClick('How It Works', '#how-it-works'); scrollToSection('how-it-works'); setMobileMenuOpen(false); }} color="inherit" underline="none" sx={{ fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif', color: '#1F2937', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', textAlign: 'right', py: 1 }}>
                  How It Works
                </MuiLink>
                <MuiLink component="button" onClick={() => { trackNavigationClick('Blog', '#blog'); scrollToSection('blog'); setMobileMenuOpen(false); }} color="inherit" underline="none" sx={{ fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif', color: '#1F2937', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', textAlign: 'right', py: 1 }}>
                  Blog
                </MuiLink>
                <MuiLink component="button" onClick={() => { trackNavigationClick('Our Mission', '#mission'); scrollToSection('mission'); setMobileMenuOpen(false); }} color="inherit" underline="none" sx={{ fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif', color: '#1F2937', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', textAlign: 'right', py: 1 }}>
                  Our Mission
                </MuiLink>
                <MuiLink component="button" onClick={() => { trackNavigationClick('FAQ', '#faq'); scrollToSection('faq'); setMobileMenuOpen(false); }} color="inherit" underline="none" sx={{ fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif', color: '#1F2937', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', textAlign: 'right', py: 1 }}>
                  FAQ
                </MuiLink>
              </Stack>
            </Box>
          </Box>
        </Box>
      )}

      {/* Hero Section */}
      <HeroSection id="hero-section" sx={{ 
          px: { xs: 2, sm: 3, md: 10, lg: 20 },
          pt: { xs: 10, sm: 12, md: 14 },
      }}>
        <Container id="hero-container" maxWidth="xl" disableGutters sx={{ 
        }}>
          <Box id="hero-content-box" sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: { xs: 4, md: 6 },
            width: '100%',
          }}>
            {/* Left Section - 1/3 width */}
            <Box id="hero-left-section" sx={{ 
              flex: { xs: 1, md: 1 }, 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              {/* Title */}
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '2.9rem' },
                  fontWeight: 700,
                  color: '#6C5DD3',
                  lineHeight: 1.1,
                  textAlign: 'center',
                }}
              >
                Drive Smart, Earn ZNLT, Build Safer Roads
              </Typography>
              
              {/* Subtitle */}
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#4B5563',
                  fontWeight: 500,
                  fontSize: { xs: '1rem', md: '1.7rem' },
                  textAlign: 'center',
                }}
              >
                ZNLT is a digital reward token designed to recognize every safe mile you drive
              </Typography>

              {/* Download Buttons */}
              <Box sx={{ 
                pt: { xs: 2, md: 3 },
                alignSelf: 'center',
                width: '100%',
              }}>
                <Stack 
                  direction="row" 
                  spacing={6} 
                  justifyContent="center"
                  sx={{
                    minHeight: '120px',
                    alignItems: 'flex-end',
                  }}
                >
                  <Button
                    component="a"
                    href="https://apps.apple.com/us/app/zenlit-drive-to-earn-crypto/id6747021447?utm_source=website&utm_medium=download_button&utm_campaign=hero"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    startIcon={<AppleIcon />}
                    onClick={() => trackAppStoreClick('hero')}
                    sx={{
                      background: 'linear-gradient(90deg, #6C5DD3 0%, #A78BFA 100%)',
                      color: 'white',
                      py: 2,
                      px: 4,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      textAlign: 'left',
                      boxShadow: '0px 4px 15px rgba(108, 93, 211, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #5A4BC4 0%, #9673E6 100%)',
                        boxShadow: '0px 6px 20px rgba(108, 93, 211, 0.4)',
                      },
                    }}
                  >
                    <Box>
                      <Typography variant="caption" display="block">
                        Download on the
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        App Store
                      </Typography>
                    </Box>
                  </Button>

                  <Button
                    component="a"
                    href="https://play.google.com/store/apps/details?id=com.telenav.telematics.app.crypto&utm_source=website&utm_medium=download_button&utm_campaign=hero"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    startIcon={<AndroidIcon />}
                    onClick={() => trackGooglePlayClick('hero')}
                    sx={{
                      background: 'linear-gradient(90deg, #6C5DD3 0%, #A78BFA 100%)',
                      color: 'white',
                      py: 2,
                      px: 4,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      textAlign: 'left',
                      boxShadow: '0px 4px 15px rgba(108, 93, 211, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #5A4BC4 0%, #9673E6 100%)',
                        boxShadow: '0px 6px 20px rgba(108, 93, 211, 0.4)',
                      },
                    }}
                  >
                    <Box>
                      <Typography variant="caption" display="block">
                        Download on the
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        Google Play
                      </Typography>
                    </Box>
                  </Button>
                </Stack>
              </Box>
            </Box>

          </Box>

          {/* Bottom centered text */}
          <Box sx={{ 
            textAlign: 'center', 
            mt: { xs: 8, md: 8 },
            mb: { xs: 2, md: 3 }
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#4B5563',
                fontWeight: 500,
                fontSize: { xs: '1.1rem', md: '1.4rem' },
                maxWidth: '100%',
                mx: 'auto'
              }}
            >
              Join a community of drivers who believe safe driving deserves real recognition and respect.<br />
              Drive smart, build your ZenScore and Earn ZNLT.<br/>
              Your data stays private, always.
            </Typography>
          </Box>
        </Container>
      </HeroSection>

      {/* How it Works Section */}
      <Box id="how-it-works" sx={{ py: 10, backgroundColor: 'white' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 8 } }}>
          <Typography 
            variant="h2" 
            align="center" 
            sx={{ 
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700, 
              color: '#322D82', 
              mb: 2 
            }}
          >
            How Zenlit Works
          </Typography>
          
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ color: '#4B5563', mb: 8, maxWidth: '600px', mx: 'auto' }}
          >
            Our simple process helps you earn rewards while becoming a safer driver
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4 
          }}>
            {features.map((feature, index) => (
              <Box key={index} sx={{ flex: 1 }}>
                <FeatureCard>
                  <Box 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: '50%', 
                      background: 'linear-gradient(90deg, #6C5DD3 0%, #A78BFA 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  
                  <Typography variant="h5" align="center" sx={{ fontWeight: 700, color: '#1F2937', mb: 2 }}>
                    {feature.title}
                  </Typography>
                  
                  <Typography variant="body1" align="center" sx={{ color: '#4B5563' }}>
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* App Preview Section */}
      <Box sx={{ py: 10, backgroundColor: '#ffffffff' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 8 } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 6 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {screenshots.map((item, index) => (
                  <PhonePreview
                    key={index}
                    onClick={() => setSelectedIndex(index)}
                    sx={{
                      cursor: 'pointer',
                      transform: index === selectedIndex ? 'scale(1.1)' : 'scale(0.9)',
                      filter: index !== selectedIndex ? 'blur(2px)' : 'none',
                      zIndex: index === selectedIndex ? 2 : 1,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box className="phone-frame">
                      <Box className="phone-screen">
                        <Box
                          component="img"
                          src={item.src}
                          alt={`App Screen ${index + 1}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </Box>
                    </Box>
                  </PhonePreview>
                ))}
              </Box>
            </Box>

            <Box sx={{ flex: 1 , aspectRatio: '1.5/1', maxWidth: 400, display: 'flex'}}>
              <StatsBox>
                <GradientButton
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    mb: 3,
                    fontSize: '1.2rem',
                    py: 2,
                    px: 4,
                  }}
                >
                  {screenshots[selectedIndex].label}
                </GradientButton>

                <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {screenshots[selectedIndex].description}
                </Typography>
              </StatsBox>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Blog Section */}
      <Box id="blog" sx={{ py: 10, backgroundColor: '#EDE9FF' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 8 } }}>
          <Typography 
            variant="h2" 
            align="center" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700, 
              color: '#1F2937', 
              mb: 2 
            }}
          >
            Blog
          </Typography>
          
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ color: '#1F2937', mb: 8, fontWeight: 500 }}
          >
            Join the Safe Driver Community
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4 
          }}>
            {blogPosts.map((post, index) => (
              <Box key={index} sx={{ flex: 1 }}>
                <Card 
                  sx={{ 
                    borderRadius: 3, 
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.15)',
                    }
                  }}
                  onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={post.image}
                    alt={post.title}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" fontWeight={700} color="#1F2937">
                        {post.title}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          backgroundColor: 'rgba(108, 93, 211, 0.1)',
                          color: '#6C5DD3',
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                        }}
                      >
                        {post.readTime}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2, flexGrow: 1 }}>
                      {(post.description || '')
                        .split(/\n\n+/)
                        .filter((p: string) => p.trim())
                        .map((paragraph: string, i: number) => (
                          <Typography
                            key={i}
                            variant="body2"
                            color="#4B5563"
                            component="p"
                            sx={{ mb: i > 0 ? 1.5 : 0, whiteSpace: 'pre-line' }}
                          >
                            {paragraph.trim()}
                          </Typography>
                        ))}
                    </Box>
                    
                    <MuiLink 
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      fontFamily= 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important'
                      sx={{ 
                        color: '#6C5DD3', 
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Read More →
                    </MuiLink>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box id="mission" sx={{ py: 10, backgroundColor: 'white' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 10 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: 2,
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '3rem', md: '6rem' },
                  fontWeight: 700,
                  color: '#1F2937',
                  mb: 4,
                }}
              >
                OUR <span style={{ color: '#6C5DD3' }}>MISSION</span>
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: '#4B5563', mb: 4, lineHeight: 1.8 }}>
                Zenlit is committed to making roads safer while rewarding drivers for positive habits. 
                We believe that every safe mile should be recognized and celebrated.
              </Typography>
              
              <Typography variant="h6" sx={{ color: '#4B5563', mb: 4, lineHeight: 1.8 }}>
                By combining cutting-edge technology with behavioral science, we&apos;re creating a community 
                of safer drivers and reducing accidents on our roads.
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box 
                    sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: '50%', 
                      backgroundColor: '#DCFCE7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SecurityIcon sx={{ color: '#16A34A', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1F2937">
                      Safer Roads
                    </Typography>
                    <Typography variant="body1" color="#4B5563">
                      For everyone
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box 
                    sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: '50%', 
                      backgroundColor: '#DBEAFE',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <RewardIcon sx={{ color: '#2563EB', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1F2937">
                      Rewards
                    </Typography>
                    <Typography variant="body1" color="#4B5563">
                      For safe drivers
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box 
                    sx={{ 
                      width: 60, 
                      height: 60, 
                      borderRadius: '50%', 
                      backgroundColor: '#E0E7FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LockIcon sx={{ color: '#6C5DD3', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1F2937">
                    Your Privacy-Always Protected
                    </Typography>
                    <Typography variant="body1" color="#4B5563">
                    We do not sell your driving data, ever.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" sx={{ py: 10, backgroundColor: '#EDE9FF' }}>
        <Container maxWidth="md">
          <Typography 
            variant="h2" 
            align="center" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700, 
              color: '#1F2937', 
              mb: 2 
            }}
          >
            Frequently Asked Questions
          </Typography>
          
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ color: '#4B5563', mb: 6 }}
          >
            Everything you need to know about Zenlit
          </Typography>

          {faqs.slice(0, showAllFaqs ? faqs.length : 5).map((faq, index) => (
            <Accordion 
              key={index}
              sx={{ 
                mb: 2, 
                borderRadius: 3,
                overflow: 'hidden',
                '&:before': { display: 'none' },
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
              '&:first-of-type': {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                '& .MuiAccordionSummary-root': {
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                },
              },
              '&:last-of-type': {
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
                '& .MuiAccordionDetails-root': {
                  borderBottomLeftRadius: 16,
                 borderBottomRightRadius: 16,
                },
              },
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: '#6C5DD3' }} />}
                onClick={() => trackFAQExpand(faq.question)}
                sx={{ py: 1, px: 3 }}
              >
                <Typography variant="h6" fontWeight={700} color="#1F2937">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  color: '#4B5563', 
                  lineHeight: 1.8,
                  '& strong, & b': {
                    fontWeight: 700,
                    color: '#1F2937',
                  },
                  '& em, & i': {
                    fontStyle: 'italic',
                  },
                  '& a': {
                    color: '#6C5DD3',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                  '& code': {
                    backgroundColor: '#F3F4F6',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '0.9em',
                  },
                  '& ul, & ol': {
                    paddingLeft: '20px',
                    margin: '8px 0',
                  },
                  '& li': {
                    margin: '4px 0',
                  },
                }}>
                  <ReactMarkdown>{faq.answer}</ReactMarkdown>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
          
          {/* More/Less Button */}
          {faqs.length > 5 && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (showAllFaqs) {
                    // Show Less clicked
                    trackShowLessClick('faq', faqs.length);
                  } else {
                    // Show More clicked
                    trackShowMoreClick('faq', faqs.length, 5);
                  }
                  setShowAllFaqs(!showAllFaqs);
                }}
                sx={{
                  borderColor: '#6C5DD3',
                  color: '#6C5DD3',
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#5540E4',
                    backgroundColor: 'rgba(108, 93, 211, 0.04)',
                  },
                }}
              >
                {showAllFaqs ? 'Show Less' : `Show More (${faqs.length - 5} more)`}
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        py: 10, 
        background: 'linear-gradient(90deg, #5540E4 0%, #A181FF 100%)',
        color: 'white',
        textAlign: 'center',
      }}>
        <Container maxWidth="md">
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700, 
              mb: 3 
            }}
          >
            Ready to Drive Smart and Get Rewarded?
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              mb: 6,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Join thousands of drivers who are making roads safer and earning rewards with every trip.
          </Typography>

          {/* Download Buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component="a"
              href="https://apps.apple.com/us/app/zenlit-drive-to-earn-crypto/id6747021447?utm_source=website&utm_medium=download_button&utm_campaign=cta"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<AppleIcon />}
              onClick={() => trackAppStoreClick('cta')}
              sx={{
                background: 'linear-gradient(90deg, #5A4BC4 0%, #8B5CF6 100%)',
                color: 'white',
                py: 2,
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                textAlign: 'left',
                boxShadow: '0px 4px 15px rgba(108, 93, 211, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #4C3DB3 0%, #7C3AED 100%)',
                  boxShadow: '0px 8px 25px rgba(108, 93, 211, 0.5)',
                },
              }}
            >
              <Box>
                <Typography variant="caption" display="block">
                  Download on the
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  App Store
                </Typography>
              </Box>
            </Button>

            <Button
              component="a"
              href="https://play.google.com/store/apps/details?id=com.telenav.telematics.app.crypto&utm_source=website&utm_medium=download_button&utm_campaign=cta"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<AndroidIcon />}
              onClick={() => trackGooglePlayClick('cta')}
              sx={{
                background: 'linear-gradient(90deg, #5A4BC4 0%, #8B5CF6 100%)',
                color: 'white',
                py: 2,
                px: 4,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                textAlign: 'left',
                boxShadow: '0px 4px 15px rgba(108, 93, 211, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #4C3DB3 0%, #7C3AED 100%)',
                  boxShadow: '0px 8px 25px rgba(108, 93, 211, 0.5)',
                },
              }}
            >
              <Box>
                <Typography variant="caption" display="block">
                  Download on the
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  Google Play
                </Typography>
              </Box>
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        py: 6, 
        backgroundColor: 'white', 
        borderTop: '1px solid #F3F4F6' 
      }}>
        <Box sx={{ px: { xs: 2, sm: 4, md: 6 } }}>
          <Box sx={{ 
            fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            maxWidth: 'xl',
            mx: 'auto'
          }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box
                  component="img"
                  src="/images/logo-coin.png"
                  alt="Zenlit Logo"
                  sx={{
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 },
                  }}
                />
                <Typography variant="h5" fontWeight={700} color="#6C5DD3">
                  Zenlit
                </Typography>
              </Box>
              <Typography variant="body1" color="#4B5563" sx={{ mb: 3 }}>
                Making roads safer while rewarding drivers for positive habits.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="small" 
                  sx={{ color: '#9CA3AF' }}
                  component="a"
                  href="https://www.facebook.com/GetZenlit"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackExternalLinkClick('facebook', 'https://www.facebook.com/GetZenlit')}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  sx={{ color: '#9CA3AF' }}
                  component="a"
                  href="https://x.com/getzenlit"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackExternalLinkClick('x_twitter', 'https://x.com/getzenlit')}
                >
                  <XIcon />
                </IconButton>

              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={600} color="#1F2937" sx={{ mb: 2 }}>
                Company
              </Typography>
              <Stack spacing={1}>
                <MuiLink 
                  href="/about" 
                  color="#4B5563" 
                  underline="none"
                  onClick={() => trackNavigationClick('About Us', '/about')}
                >
                  About Us
                </MuiLink>
                <MuiLink 
                  href="/contact" 
                  color="#4B5563" 
                  underline="none"
                  onClick={() => trackNavigationClick('Contact Us', '/contact')}
                >
                  Contact Us
                </MuiLink>
                <MuiLink 
                  component="button"
                  onClick={() => {
                    trackNavigationClick('Support & FAQ', '#faq');
                    scrollToSection('faq');
                  }}
                  color="#4B5563" 
                  underline="none"
                  sx={{ 
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                    padding: 0,
                    margin: 0
                  }}
                >
                  Support & FAQ
                </MuiLink>
                <MuiLink 
                  component="button"
                  onClick={() => {
                    trackNavigationClick('Blog', '#blog');
                    scrollToSection('blog');
                  }}
                  color="#4B5563" 
                  underline="none"
                  sx={{ 
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                    padding: 0,
                    margin: 0
                  }}
                >
                  Blog
                </MuiLink>
              </Stack>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={600} color="#1F2937" sx={{ mb: 2 }}>
                Legal
              </Typography>
              <Stack spacing={1}>
                <MuiLink 
                  href="/privacy" 
                  color="#4B5563" 
                  underline="none"
                  onClick={() => trackNavigationClick('Privacy Policy', '/privacy')}
                >
                  Privacy Policy
                </MuiLink>
                <MuiLink 
                  href="/terms" 
                  color="#4B5563" 
                  underline="none"
                  onClick={() => trackNavigationClick('Terms of Service', '/terms')}
                >
                  Terms of Service
                </MuiLink>
              </Stack>
            </Box>

          </Box>

          <Divider sx={{ my: 4 }} />
          
          <Typography 
            variant="body2" 
            color="#6B7280" 
            align="center"
          >
            © 2025 Zenlit. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const blogPosts = await getAllBlogPosts();
  const faqs = await getFAQData();
  
  return {
    props: {
      blogPosts,
      faqs,
    },
  };
};

export default SPTDrivePortal;
